export const getApiBaseURL = (requestPath: string) => {
	const config = useNuxtApp().$config
	const scmPrefixes = ['inbound-delivery/api', 'purchase/api', 'suppliers/api']
	const logisticsPrefixes = [
		'api/employee/logistic-units',
		'api/employee/get-delivery-orders',
		'api/employee/delivery',
	]
	const wmsPrefixes = ['warehouse/api']

	for (const prefix of scmPrefixes) {
		if (requestPath.includes(prefix)) {
			return config.public.scmApiBaseURL
		}
	}

	for (const prefix of logisticsPrefixes) {
		if (requestPath.includes(prefix)) {
			return config.public.logisticsApiBaseURL
		}
	}

	for (const prefix of wmsPrefixes) {
		if (requestPath.includes(prefix)) {
			return config.public.wmsApiBaseURL
		}
	}

	return config.public.mesApiBaseURL
}

const parseFilenameFromContentDisposition = (contentDisposition: string, fallback: string): string => {
	const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
	if (filenameStarMatch?.[1]) {
		return decodeURIComponent(filenameStarMatch[1].trim())
	}

	const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
	if (filenameMatch?.[1]) {
		return filenameMatch[1].trim()
	}

	return fallback
}

const triggerBlobDownload = (blob: Blob, filename: string) => {
	const url = window.URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.setAttribute('download', filename)
	document.body.appendChild(link)
	link.click()
	link.remove()
	window.URL.revokeObjectURL(url)
}

export const apiCommon = async <T>(
	request: any,
	options: any,
	lang = 'pl',
	formData = false,
): Promise<IApiCommonModel<T>> => {
	const setParameters = () => {
		const xsrfToken = useCookie('XSRF-TOKEN')
		const config = useNuxtApp().$config
		const { headers: customHeaders, ...customOptions } = options ?? {}

		const headers = {
			accept: customOptions?.isFileDownload ? '*/*' : 'application/json',
			'Accept-Language': lang,
			'Access-Control-Expose-Headers': 'Content-Disposition',
			'X-Employee-System-Type': config.public.employeeSystemType,
			...customHeaders,
		}

		if (!formData) headers['Content-Type'] = 'application/json'

		if (xsrfToken?.value) {
			headers['X-XSRF-TOKEN'] = xsrfToken.value
		}

		try {
			if (
				request.startsWith(`${ApiModule.PRODUCTION_PLANNING}/api/`) ||
				request.startsWith(`${ApiModule.DEMAND_PLANNING}/api/`)
			) {
				const facilityIdentifier = useFacilityStore().getSelectedIdentifier
				if (facilityIdentifier) {
					headers['X-Facility-Identifier'] = facilityIdentifier
				}
			}
		} catch {
			/* silent — store may not be ready during early init */
		}

		const obj = {
			baseURL: getApiBaseURL(request),
			headers,
			credentials: 'include',
			mode: 'cors',
			...customOptions,
		}

		if (customOptions?.isFileDownload) {
			obj.responseType = 'blob'
		}

		return obj
	}

	const isCookieRequest = () => request === 'sanctum/csrf-cookie'

	const fetchData = async () => {
		try {
			const response = await $fetch.raw(`/${request}`, setParameters())

			if (options?.isFileDownload && response._data instanceof Blob) {
				const defaultFilename = options?.downloadFilename || 'download.zip'
				const contentDisposition = response.headers.get('content-disposition')
				const filename = contentDisposition
					? parseFilenameFromContentDisposition(contentDisposition, defaultFilename)
					: defaultFilename

				triggerBlobDownload(response._data, filename)

				return { data: response._data } as IApiCommonModel<T>
			}

			const data = response._data as IApiCommon<T>
			return data?.meta
				? ({ ...data, meta: ApiMeta(data.meta) } as IApiCommonModel<T>)
				: (data as IApiCommonModel<T>)
		} catch (error: any) {
			const receivedData: IError = { ...error.data, status: error?.response?.status }

			throw Object.assign(new Error(receivedData.message ?? JSON.stringify(receivedData)), {
				...receivedData,
			})
		}
	}

	try {
		if (!isCookieRequest()) {
			const xsrfToken = useCookie('XSRF-TOKEN')
			if (!xsrfToken?.value) {
				await ApiService.setCookies()
			}
		}
		return await fetchData()
	} catch (error: any) {
		if (error.status === EHttpStatusCodes.HTTP_STATUS_CSRF_TOKEN_MISMATCH && !isCookieRequest()) {
			await ApiService.setCookies()
			return await fetchData()
		}
		throw error
	}
}
